import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { collection, deleteDoc, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/Firebase';
import { IBalance } from '../../interfaces/StoreTypes';
import { ITime } from '../../interfaces/Types';
import AbsenceUtils from '../../utils/AbsenceUtils';
import DayjsUtils from '../../utils/DayjsUtils';
import TimeUtils from '../../utils/TimeUtils';
import {
  setEvaluateTimes,
  setFirstTimeDay,
  setSumLastMonth,
  setSumMonth,
  setSumYear,
  setTimes,
  setTimesLastRefresh,
  setTimesLoading,
} from '../slices/timesSlice';

export const getTimes = createAsyncThunk('times/all', async (action: any, thunkAPI: any) => {
  thunkAPI.dispatch(setTimesLoading(true));

  const { user } = thunkAPI.getState().auth;

  const result: ITime[] = [];

  if (action === undefined) action = TimeUtils.getDefaultTimeRange();

  const ref = collection(db, user.uid);
  const q = query(ref, where('timestamp', '>=', action.from), where('timestamp', '<=', action.to));
  const response = await getDocs(q);
  if (response.docs !== undefined) {
    response.docs.forEach((item) => {
      result.push(item.data() as ITime);
    });
  }

  // Calculate sums per year and month and last month
  thunkAPI.dispatch(setSumYear(TimeUtils.sumOfThisYear(result)));
  thunkAPI.dispatch(setSumMonth(TimeUtils.sumOfCurrentMonth(result)));
  thunkAPI.dispatch(setSumLastMonth(TimeUtils.sumOfCurrentMonth(result)));

  // Find first day of time entries
  thunkAPI.dispatch(setFirstTimeDay(TimeUtils.findfirstDay(result)));

  // Set times and loading state
  thunkAPI.dispatch(setTimes(result));
  thunkAPI.dispatch(setTimesLoading(false));
  thunkAPI.dispatch(setTimesLastRefresh(dayjs().unix()));
});

export const addTimes = createAsyncThunk('times/add', async (action: any, thunkAPI: any) => {
  const times = action as ITime[];
  const { user } = thunkAPI.getState().auth;

  const batch = writeBatch(db);
  times.forEach((time) => {
    batch.set(doc(db, `${user!.uid}/${time.day}`), {
      day: time.day,
      workingTime: time.workingTime,
      availableTime: time.availableTime,
      notes: time.notes,
      timestamp: TimeUtils.dateStringToTimestamp(time.day),
      workday: time.workday,
    });
  });

  batch
    .commit()
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      thunkAPI.dispatch(getTimes(undefined));
    });
});

export const deleteTime = createAsyncThunk('time/delete', async (action: any, thunkAPI: any) => {
  const { user } = thunkAPI.getState().auth;
  deleteDoc(doc(db, `${user!.uid}/${action}`))
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      thunkAPI.dispatch(getTimes(undefined));
    });
});

export const evaluateTimes = createAsyncThunk('evaluate/times', async (action: any, thunkAPI: any) => {
  const profile = thunkAPI.getState().auth.profile;
  const times = thunkAPI.getState().times.times;
  const absences = thunkAPI.getState().absences.absences;

  const dataset: IBalance[] = [];

  // iterate over each month
  for (let i = 0; i < 12; i++) {
    const year = DayjsUtils.startOfCurrentYear().year();
    const date = DayjsUtils.dateByMonthAndYear(i, year);

    const from = DayjsUtils.startOfMonthByDay(date).unix();
    const to = DayjsUtils.endOfMonthByDay(date).unix();

    // result object for the current month
    const result: IBalance = {
      year: year,
      month: i,
      range: {
        from: from,
        to: to,
      },
    };

    // if >to< is in future, stop iteration
    if (to > DayjsUtils.todayEnd().unix()) {
      dataset.push(result);
      continue;
    }

    // filter absences for the current month
    const absencesInMonth = AbsenceUtils.getAbsencesByTimestampRange(absences, from, to);
    result['days'] = {};
    result['days']['absences'] = absencesInMonth.length;

    // calculate workdays in the current month
    const workDaysInMonth = TimeUtils.workDaysInMonth(from, to, profile);
    result['days']['total'] = workDaysInMonth;

    // calculate target values for the current month
    const targetDaysInMonth = workDaysInMonth - absencesInMonth.length;
    result['days']['personal'] = targetDaysInMonth;

    const targetWorkingMinutesInMonth = targetDaysInMonth * profile.workingtime;
    result['workingTime'] = {};
    result['workingTime']['target'] = targetWorkingMinutesInMonth;

    const targetAvailableMinutesInMonth = targetDaysInMonth * profile.availabletime;
    result['availableTime'] = {};
    result['availableTime']['target'] = targetAvailableMinutesInMonth;

    // filter times for the current month
    const timesInMonth = TimeUtils.filterByTimestampRange(from, to, times);

    // calculate sums for the current month
    const sumsOfMonth = TimeUtils.calculateSums(timesInMonth);
    result['workingTime']['actual'] = sumsOfMonth.workingTime;
    result['availableTime']['actual'] = sumsOfMonth.availableTime;

    // calculate balances for the current month
    const workingTimeBalanceInMonth = sumsOfMonth.workingTime - targetWorkingMinutesInMonth;
    result['workingTime']['balance'] = workingTimeBalanceInMonth;

    const availableTimeBalanceInMonth = sumsOfMonth.availableTime - targetAvailableMinutesInMonth;
    result['availableTime']['balance'] = availableTimeBalanceInMonth;

    dataset.push(result);
  }

  thunkAPI.dispatch(setEvaluateTimes(dataset));
});
